import React from "react";
import { Card, Select, Input, Row, Col, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Category, Location } from "../hooks/useFinanceData";

const { Option } = Select;

interface FinanceFiltersProps {
  categories: Category[];
  allSuppliers: Array<{ _id: string; name: string; categoryName: string }>;
  selectedCategories: string[];
  selectedSuppliers: string[];
  searchQuery: string;
  onCategoryChange: (categoryIds: string[]) => void;
  onSupplierChange: (supplierIds: string[]) => void;
  onSearchChange: (query: string) => void;
}

export const FinanceFilters: React.FC<FinanceFiltersProps> = ({
  categories,
  allSuppliers,
  selectedCategories,
  selectedSuppliers,
  searchQuery,
  onCategoryChange,
  onSupplierChange,
  onSearchChange,
}) => {
  const { t } = useTranslation();

  return (
    <Card bordered={false} style={{ marginBottom: 16 }}>
      <Row gutter={[16, 16]}>
        {/* Category Filter */}
        <Col xs={24} sm={12} md={8}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: "#8c8c8c" }}>
              {t("Category")}
            </div>
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder={t("All categories")}
              style={{ width: "100%" }}
              value={
                selectedCategories.length > 0 ? selectedCategories : undefined
              }
              onChange={(values: string[]) => {
                if (values.length > 3) {
                  message.warning(t("Maximum 3 categories can be selected"));
                  return;
                }
                onCategoryChange(values);
              }}
              maxTagCount="responsive"
              filterOption={(input, option) => {
                if (!option || !option.children) return false;
                const label = String(option.children);
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {categories.map((cat) => (
                <Option key={cat._id} value={cat._id}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </div>
        </Col>

        {/* Supplier Filter */}
        <Col xs={24} sm={12} md={8}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: "#8c8c8c" }}>
              {t("Supplier")}
            </div>
            <Select
              mode="multiple"
              allowClear
              placeholder={t("All suppliers")}
              style={{ width: "100%" }}
              value={
                selectedSuppliers.length > 0 ? selectedSuppliers : undefined
              }
              onChange={onSupplierChange}
              maxTagCount="responsive"
              showSearch
              filterOption={(input, option) => {
                if (!option || !option.children) return false;
                const label = String(option.children);
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {allSuppliers.map((sup) => (
                <Option key={sup._id} value={sup._id}>
                  {sup.name} ({sup.categoryName})
                </Option>
              ))}
            </Select>
          </div>
        </Col>

        {/* Search */}
        <Col xs={24} sm={24} md={8}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: "#8c8c8c" }}>
              {t("Search")}
            </div>
            <Input
              placeholder={t("Search categories or suppliers...")}
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              allowClear
            />
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default FinanceFilters;
